/**
 * 西暦 ⇔ 和暦 変換と事業年度ラベル。
 *
 * - 日付文字列は ISO 形式 `YYYY-MM-DD`（API の date 型）を基本とする。
 * - タイムゾーンの影響を受けないよう、内部では年月日の数値で扱う（Date は使うがローカル日付のみ参照）。
 */

export type EraName = '明治' | '大正' | '昭和' | '平成' | '令和';

interface Era {
  name: EraName;
  short: string; // M / T / S / H / R
  /** 開始日（この日から当該元号） */
  start: { y: number; m: number; d: number };
}

/** 新しい順 */
const ERAS: readonly Era[] = [
  { name: '令和', short: 'R', start: { y: 2019, m: 5, d: 1 } },
  { name: '平成', short: 'H', start: { y: 1989, m: 1, d: 8 } },
  { name: '昭和', short: 'S', start: { y: 1926, m: 12, d: 25 } },
  { name: '大正', short: 'T', start: { y: 1912, m: 7, d: 30 } },
  { name: '明治', short: 'M', start: { y: 1868, m: 10, d: 23 } },
];

export interface YMD {
  y: number;
  m: number;
  d: number;
}

export interface WarekiParts {
  era: EraName;
  eraShort: string;
  /** 元号年（1 = 元年） */
  year: number;
  month: number;
  day: number;
}

const ISO_RE = /^(\d{4})-(\d{1,2})-(\d{1,2})/;

/** `YYYY-MM-DD` / Date を {y,m,d} に。解釈できなければ null */
export function toYMD(input: string | Date | null | undefined): YMD | null {
  if (input == null || input === '') return null;
  if (input instanceof Date) {
    if (Number.isNaN(input.getTime())) return null;
    return { y: input.getFullYear(), m: input.getMonth() + 1, d: input.getDate() };
  }
  const m = ISO_RE.exec(input);
  if (!m) return null;
  const ymd = { y: Number(m[1]), m: Number(m[2]), d: Number(m[3]) };
  return isValidYMD(ymd) ? ymd : null;
}

export function isValidYMD({ y, m, d }: YMD): boolean {
  if (m < 1 || m > 12 || d < 1) return false;
  const last = new Date(y, m, 0).getDate();
  return d <= last;
}

function cmp(a: YMD, b: YMD): number {
  return a.y - b.y || a.m - b.m || a.d - b.d;
}

/** 西暦日付 → 和暦の各要素。明治以前は null */
export function toWarekiParts(input: string | Date | null | undefined): WarekiParts | null {
  const ymd = toYMD(input);
  if (!ymd) return null;
  const era = ERAS.find((e) => cmp(ymd, e.start) >= 0);
  if (!era) return null;
  return {
    era: era.name,
    eraShort: era.short,
    year: ymd.y - era.start.y + 1,
    month: ymd.m,
    day: ymd.d,
  };
}

export interface WarekiFormatOptions {
  /** 1年を「元年」と表記する（既定 true） */
  gannen?: boolean;
  /** 'long' = 令和8年4月1日 / 'short' = R8.4.1 / 'year' = 令和8年 / 'month' = 令和8年4月 */
  style?: 'long' | 'short' | 'year' | 'month';
}

function eraYearText(year: number, gannen: boolean): string {
  return year === 1 && gannen ? '元' : String(year);
}

/**
 * 西暦 → 和暦文字列。
 * @example toWareki('2026-04-01') // => '令和8年4月1日'
 * @example toWareki('2019-05-01') // => '令和元年5月1日'
 * @example toWareki('2026-04-01', { style: 'short' }) // => 'R8.4.1'
 */
export function toWareki(input: string | Date | null | undefined, opts: WarekiFormatOptions = {}): string {
  const p = toWarekiParts(input);
  if (!p) return '';
  const { gannen = true, style = 'long' } = opts;
  switch (style) {
    case 'short':
      return `${p.eraShort}${p.year}.${p.month}.${p.day}`;
    case 'year':
      return `${p.era}${eraYearText(p.year, gannen)}年`;
    case 'month':
      return `${p.era}${eraYearText(p.year, gannen)}年${p.month}月`;
    default:
      return `${p.era}${eraYearText(p.year, gannen)}年${p.month}月${p.day}日`;
  }
}

const WAREKI_RE = /^(明治|大正|昭和|平成|令和|[MTSHR])\s*(元|\d{1,2})\s*[年.\-/]\s*(\d{1,2})\s*[月.\-/]\s*(\d{1,2})\s*日?$/;

/**
 * 和暦文字列 → `YYYY-MM-DD`。解釈できない・存在しない日付は null。
 * 受け付ける形式: `令和8年4月1日` `令和元年5月1日` `R8.4.1` `H31/4/30`
 */
export function fromWareki(text: string): string | null {
  const m = WAREKI_RE.exec(text.trim());
  if (!m) return null;
  const [, eraToken, yearToken, monthToken, dayToken] = m;
  const era = ERAS.find((e) => e.name === eraToken || e.short === eraToken?.toUpperCase());
  if (!era) return null;
  const eraYear = yearToken === '元' ? 1 : Number(yearToken);
  const ymd: YMD = { y: era.start.y + eraYear - 1, m: Number(monthToken), d: Number(dayToken) };
  if (!isValidYMD(ymd)) return null;
  // 元号の範囲外（例: 令和1年4月1日 は平成）を弾く
  const parts = toWarekiParts(formatISO(ymd));
  if (!parts || parts.era !== era.name) return null;
  return formatISO(ymd);
}

export function formatISO({ y, m, d }: YMD): string {
  return `${String(y).padStart(4, '0')}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

/** 日付が属する事業年度（4月始まり）。2026-03-31 → 2025 */
export function fiscalYearOf(input: string | Date): number | null {
  const ymd = toYMD(input);
  if (!ymd) return null;
  return ymd.m >= 4 ? ymd.y : ymd.y - 1;
}

/**
 * 事業年度の和暦ラベル。改元年度は新元号で表記する（2019 → 令和元年度、1988 → 昭和63年度）。
 * @example fiscalYearLabel(2026) // => '令和8年度'
 * @example fiscalYearLabel(2026, { withSeireki: true }) // => '令和8年度（2026年度）'
 */
export function fiscalYearLabel(year: number, opts: { withSeireki?: boolean } = {}): string {
  // 開始年の 12/31 時点の元号を用いる（実務慣行に一致）
  const base = toWareki(`${String(year).padStart(4, '0')}-12-31`, { style: 'year' });
  const label = `${base}度`;
  return opts.withSeireki ? `${label}（${year}年度）` : label;
}

/** 事業年度の期間（4/1〜翌3/31） */
export function fiscalYearRange(year: number): { startDate: string; endDate: string } {
  return { startDate: `${year}-04-01`, endDate: `${year + 1}-03-31` };
}

const WEEKDAYS = ['日', '月', '火', '水', '木', '金', '土'] as const;

/** 曜日（日〜土） */
export function weekdayJa(input: string | Date): string {
  const ymd = toYMD(input);
  if (!ymd) return '';
  return WEEKDAYS[new Date(ymd.y, ymd.m - 1, ymd.d).getDay()] ?? '';
}

/** 今日（ローカル）の `YYYY-MM-DD` */
export function todayISO(now: Date = new Date()): string {
  return formatISO({ y: now.getFullYear(), m: now.getMonth() + 1, d: now.getDate() });
}
